// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { StoreBaseContainerProps } from '../../../../common/types/index.tsx';
import { createDataResponse, createErrorResponse } from '../../../../common/utils/store/index.tsx';
import { multiplePromiseSettler } from '../../utils/index.tsx';
import * as endpoints from '../endpoints/index.tsx';
import type { IntegrationsState } from '../types.tsx';

const getIntegrationCounts =
	(): Action<IntegrationsState, StoreBaseContainerProps> =>
	({ setState, getState }, { cloudId }) => {
		const state = getState();
		setState({
			counts: {
				...state.counts,
				loading: true,
			},
		});
		return endpoints
			.getIntegrationCounts(cloudId)
			.then((res) => setState({ counts: createDataResponse(res) }))
			.catch((err) => {
				setState({ counts: createErrorResponse(err) });
				throw err;
			});
	};

const getSyncedIntegrations =
	(): Action<IntegrationsState, StoreBaseContainerProps> =>
	({ setState, getState }, { cloudId }) => {
		const state = getState();
		setState({
			syncedIntegrations: {
				...state.syncedIntegrations,
				loading: true,
			},
		});
		return endpoints
			.getSyncedIntegrations(cloudId)
			.then((res) => setState({ syncedIntegrations: createDataResponse(res) }))
			.catch((err) => {
				setState({ syncedIntegrations: createErrorResponse(err) });
				throw err;
			});
	};

const getDiscontinuedIntegrations =
	(): Action<IntegrationsState, StoreBaseContainerProps> =>
	({ setState, getState }, { cloudId }) => {
		const state = getState();
		setState({
			discontinuedIntegrations: {
				...state.discontinuedIntegrations,
				loading: true,
			},
		});
		return endpoints
			.getDiscontinuedIntegrations(cloudId)
			.then((res) => setState({ discontinuedIntegrations: createDataResponse(res) }))
			.catch((err) => {
				setState({ discontinuedIntegrations: createErrorResponse(err) });
				throw err;
			});
	};

const getDeprecatedIntegrations =
	(): Action<IntegrationsState, StoreBaseContainerProps> =>
	({ setState, getState }, { cloudId }) => {
		const state = getState();
		setState({
			deprecatedIntegrations: {
				...state.deprecatedIntegrations,
				loading: true,
			},
		});
		return endpoints
			.getDeprecatedIntegrations(cloudId)
			.then((res) => setState({ deprecatedIntegrations: createDataResponse(res) }))
			.catch((err) => {
				setState({ deprecatedIntegrations: createErrorResponse(err) });
				throw err;
			});
	};

const getIncomingCallIntegrations =
	(): Action<IntegrationsState, StoreBaseContainerProps> =>
	({ setState, getState }, { cloudId }) => {
		const state = getState();
		setState({
			incomingCallIntegrations: {
				...state.incomingCallIntegrations,
				loading: true,
			},
		});
		return endpoints
			.getIncomingCallIntegrations(cloudId)
			.then((res) => setState({ incomingCallIntegrations: createDataResponse(res) }))
			.catch((err) => {
				setState({ incomingCallIntegrations: createErrorResponse(err) });
				throw err;
			});
	};

const initialize =
	(): Action<IntegrationsState, StoreBaseContainerProps, Promise<PromiseSettledResult<void>[]>> =>
	({ dispatch }) =>
		Promise.allSettled([
			dispatch(getIntegrationCounts()),
			dispatch(getSyncedIntegrations()),
			dispatch(getDeprecatedIntegrations()),
			dispatch(getDiscontinuedIntegrations()),
		]).then(multiplePromiseSettler);

const initializeForAdmin =
	(): Action<IntegrationsState, StoreBaseContainerProps, Promise<PromiseSettledResult<void>[]>> =>
	({ dispatch }) =>
		Promise.allSettled([
			dispatch(getIntegrationCounts()),
			dispatch(getSyncedIntegrations()),
			dispatch(getDeprecatedIntegrations()),
			dispatch(getDiscontinuedIntegrations()),
			dispatch(getIncomingCallIntegrations()),
		]).then(multiplePromiseSettler);

export const actions = {
	initialize,
	initializeForAdmin,
};
